// e2e.ts — Test-only bridge between the headless-Chrome playtest harness and the
// running SPA. The galaxy map is a WebGL <Canvas> with no DOM handles: stars are
// raycast-picked spheres, mode transitions ride scroll-wheel gestures, and there
// are no data-testid hooks. Driving that reliably from Playwright would mean
// synthesizing pointer/wheel events at guessed pixel coordinates.
//
// Instead, when the page is loaded with `?e2e=1`, the app publishes a small,
// stable control surface on `window.__farstars`: the focus/hover actions, a
// snapshot of the focus state, the generated systems, a 3D→screen projector (so
// the harness CAN drive a real mouse hover when it wants to exercise the actual
// pointer path), and the live three.js renderer stats for the perf report.
//
// This is INERT in production: nothing touches `window` unless `?e2e=1` is
// present, so a normal player session never sees it. See products/farstars/web/
// e2e/playtest.mjs for the consumer.

import type { Object3D } from 'three';
import type { StarSystem } from './types';

export const E2E_ENABLED =
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('e2e');

/** Pixel coordinates within the canvas/viewport (top-left origin). */
export interface ScreenPoint {
  x: number;
  y: number;
}

/** Snapshot of the focus hierarchy the harness asserts against. */
export interface E2EState {
  /** The single focused-entity id; the by-kind fields below are derived from it. */
  focusedId: string | null;
  focusedSystemId: string | null;
  focusedPlanetId: string | null;
  focusedMoonId: string | null;
  selectedSingularityId: string | null;
  hoveredId: string | null;
}

/** A frame's worth of three.js renderer stats, for the perf report. */
export interface E2ERenderInfo {
  calls: number;
  triangles: number;
  geometries: number;
  textures: number;
  programs: number;
}

/** The control surface published on window.__farstars under ?e2e=1. */
export interface E2EApi {
  /** True once the galaxy has loaded and the focus actions are wired. */
  ready?: boolean;
  /** True once the <Canvas> has mounted and screenOf/renderInfo are wired. */
  canvasReady?: boolean;
  /** Focus any entity by id (system/planet/singularity); null = galaxy. */
  focus?: (id: string | null) => void;
  // By-kind aliases of focus(), kept for harness compatibility:
  focusSystem?: (id: string | null) => void;
  focusPlanet?: (id: string | null) => void;
  selectSingularity?: (id: string | null) => void;
  /** Select a ship (open its info pane), or null to deselect. */
  selectShip?: (id: string | null) => void;
  hover?: (id: string | null) => void;
  getState?: () => E2EState;
  systems?: StarSystem[];
  /** The galaxy's hyperlane graph (undirected {a,b} system-id links). */
  hyperlanes?: { a: string; b: string }[];
  /** Project a world position [x,y,z] to viewport pixels (null if behind camera). */
  screenOf?: (worldPos: [number, number, number]) => ScreenPoint | null;
  /** Project a registered body's LIVE world position to viewport pixels (null if
   *  unregistered or behind the camera). Unlike screenOf, this reads where the body
   *  actually is right now — a planet orbits, so its pixel moves with the clock — so
   *  the harness can aim a real cursor at it and watch hover follow as it moves. */
  screenOfPlanet?: (id: string) => ScreenPoint | null;
  renderInfo?: () => E2ERenderInfo;
  /** Pin the simulation clock to a fixed game-time so screenshots are stable. */
  freezeClock?: (time?: number) => void;
  /** Set the simulation speed multiplier (re-animates orbits for the probe). */
  setClockSpeed?: (speed: number) => void;
  /** Test-only: set to [] and CameraRig pushes the focused planet's on-screen
   *  pixel position each rendered frame (skew-free ground truth), for the
   *  tracking-smoothness probe. Leave unset to disable. */
  track?: ScreenPoint[];
  /** Test-only A/B switch: when true, CameraRig skips the same-frame controls
   *  update so the playtest can measure the deployed one-frame follow lag. */
  skipFollowUpdate?: boolean;
  /** Test-only: a summary of the live economy ships (null/[] until the state WS streams),
   *  so the harness can verify ships are present and locate one to inspect. */
  getSimShips?: () => Array<{ id: string; systemId: string; phase: string; originId: string; destId: string }>;
}

declare global {
  interface Window {
    __farstars?: E2EApi;
  }
}

/** Merge a patch into window.__farstars. No-op unless ?e2e=1. */
export function e2eAssign(patch: E2EApi): void {
  if (!E2E_ENABLED || typeof window === 'undefined') {
    return;
  }
  window.__farstars = Object.assign(window.__farstars ?? {}, patch);
}

// Test-only registry of orbiting body scene-nodes, keyed by id, so the playtest can
// read a body's live on-screen position as it orbits without re-deriving the Kepler
// math in the harness. Planet populates it under ?e2e=1; E2EBridge.screenOfPlanet
// projects the looked-up node. Inert in production (the registrar no-ops).
const e2eBodies = new Map<string, Object3D>();

/** Register (node) or unregister (null) a body's scene node under its id. No-op
 *  unless ?e2e=1. */
export function e2eRegisterBody(id: string, node: Object3D | null): void {
  if (!E2E_ENABLED) {
    return;
  }
  if (node) {
    e2eBodies.set(id, node);
  } else {
    e2eBodies.delete(id);
  }
}

/** The registered scene node for a body id, or undefined. */
export function e2eBodyNode(id: string): Object3D | undefined {
  return e2eBodies.get(id);
}
